import { Injectable } from '@angular/core';
import { CustomRouterNavigatedAction } from '../../../core/custom-route-serializer';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ROUTER_NAVIGATED } from '@ngrx/router-store';

import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators';

import { ToastrService } from 'ngx-toastr';

import { SETTINGS_ROUTES } from '../../settings/settings.routes';
import * as umParticipantActions from './um-participant.actions';
import { UmParticipantService } from '../../services/um-participant/um-participant.service';
import { DashboardCamelCasePipe } from '../../../core/pipes/dashboard-camel-case/dashboard-camel-case.pipe';
import { UM_PARTICIPANTS_PAGE_SIZE } from '../../../core/dashboard-constants';
import { DASHBOARD_ROUTES } from '../../dashboard.routes';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';

@Injectable()
export class UmParticipantEffects {
  umParticipantsRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter(
        (route: CustomRouterNavigatedAction) =>
          route.payload.routerState.url.includes(DASHBOARD_ROUTES.SETTINGS) &&
          route.payload.routerState.url.includes(SETTINGS_ROUTES.PARTICIPANTS) &&
          !route.payload.routerState.params.hasOwnProperty('id')
      ),
      map(() => umParticipantActions.getUmParticipants({ pageNumber: 1, pageSize: UM_PARTICIPANTS_PAGE_SIZE }))
    )
  );

  umSingleParticipantRoute$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CustomRouterNavigatedAction>(ROUTER_NAVIGATED),
      filter(
        (route: CustomRouterNavigatedAction) =>
          route.payload.routerState.url.includes(DASHBOARD_ROUTES.SETTINGS) &&
          route.payload.routerState.url.includes(SETTINGS_ROUTES.PARTICIPANTS) &&
          route.payload.routerState.params.hasOwnProperty('id')
      ),
      map((action) => umParticipantActions.selectUmParticipant({ participantId: action.payload.routerState.params.id }))
    )
  );

  getUmParticipants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.getUmParticipants),
      switchMap(({ pageNumber, pageSize, filterValue }) => {
        return this.umParticipantService.getUmParticipants(pageNumber, pageSize, filterValue).pipe(
          map((umParticipantResponse) => {
            return umParticipantActions.getUmParticipantsSuccess({
              umParticipantResponse: this.dashboardCamelCasePipe.keysToCamelCase(umParticipantResponse),
            });
          }),
          catchError(() => of(umParticipantActions.getUmParticipantsError()))
        );
      })
    )
  );

  getUmParticipantsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.getUmParticipantsError),
        tap(() => this.toastrService.error('Unable to load Participants'))
      ),
    { dispatch: false }
  );

  selectUmParticipant$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.selectUmParticipant),
      switchMap(({ participantId }) => {
        return this.umParticipantService.getUmParticipantById(participantId).pipe(
          map((umParticipant) => {
            return umParticipantActions.selectUmParticipantSuccess({
              selectedUmParticipant: this.dashboardCamelCasePipe.keysToCamelCase(umParticipant),
            });
          }),
          catchError(() => of(umParticipantActions.selectUmParticipantError()))
        );
      })
    )
  );

  selectUmParticipantsError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.selectUmParticipantError),
        tap(() => this.toastrService.error('Unable to load selected Participant'))
      ),
    { dispatch: false }
  );

  deleteParticipantFromSystemAndKeepData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.deleteParticipantFromSystemAndKeepData),
      switchMap(({ participantId }) => {
        return this.umParticipantService.deleteParticipantFromSystemAndKeepData(participantId).pipe(
          map(() => umParticipantActions.deleteParticipantFromSystemAndKeepDataSuccess()),
          catchError(() => of(umParticipantActions.deleteParticipantFromSystemAndKeepDataError()))
        );
      })
    )
  );

  deleteParticipantFromSystemAndKeepDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.deleteParticipantFromSystemAndKeepDataSuccess),
        tap(() => {
          this.toastrService.success('Successfully deleted Participant from the System and kept his data as Anonymous');
          this.router.navigate([`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.PARTICIPANTS}`]);
        })
      ),
    { dispatch: false }
  );

  deleteParticipantFromSystemAndKeepDataError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.deleteParticipantFromSystemAndKeepDataError),
        tap(() => this.toastrService.error('Error while delete Participant from the System'))
      ),
    { dispatch: false }
  );

  deleteParticipantFromSystemAndRemoveData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.deleteParticipantFromSystemAndRemoveData),
      switchMap(({ participantId }) => {
        return this.umParticipantService.deleteParticipantFromSystemAndRemoveData(participantId).pipe(
          map(() => umParticipantActions.deleteParticipantFromSystemAndRemoveDataSuccess()),
          catchError(() => of(umParticipantActions.deleteParticipantFromSystemAndRemoveDataError()))
        );
      })
    )
  );

  deleteParticipantFromSystemAndRemoveDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.deleteParticipantFromSystemAndRemoveDataSuccess),
        tap(() => {
          this.router.navigate([`/${DASHBOARD_ROUTES.SETTINGS}/${SETTINGS_ROUTES.PARTICIPANTS}`]);
          this.toastrService.success('Successfully deleted Participant from the System with all his data');
        })
      ),
    { dispatch: false }
  );

  deleteParticipantFromSystemAndRemoveDataError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.deleteParticipantFromSystemAndRemoveDataError),
        tap(() => this.toastrService.error('Error while delete Participant from the System'))
      ),
    { dispatch: false }
  );

  withdrawParticipantFromStudyAndKeepData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.withdrawParticipantFromStudyAndKeepData),
      switchMap(({ studyId, participantId }) => {
        return this.umParticipantService.withdrawParticipantFromStudyAndKeepData(studyId, participantId).pipe(
          map((umParticipant) =>
            umParticipantActions.withdrawParticipantFromStudyAndKeepDataSuccess({ umParticipant })
          ),
          catchError(() => of(umParticipantActions.withdrawParticipantFromStudyAndKeepDataError()))
        );
      })
    )
  );

  withdrawParticipantFromStudyAndKeepDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.withdrawParticipantFromStudyAndKeepDataSuccess),
        tap(({ umParticipant }) => {
          this.toastrService.success(`Successfully removed Participant from the Study and kept his data`);
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  withdrawParticipantFromStudyAndKeepDataError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.withdrawParticipantFromStudyAndKeepDataError),
        tap(() => this.toastrService.error('Error while delete Participant from the Study'))
      ),
    { dispatch: false }
  );

  withdrawParticipantFromStudyAndRemoveData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.withdrawParticipantFromStudyAndRemoveData),
      switchMap(({ studyId, participantId }) => {
        return this.umParticipantService.withdrawParticipantFromStudyAndRemoveData(studyId, participantId).pipe(
          map((umParticipant) =>
            umParticipantActions.withdrawParticipantFromStudyAndRemoveDataSuccess({ umParticipant })
          ),
          catchError(() => of(umParticipantActions.withdrawParticipantFromStudyAndRemoveDataError()))
        );
      })
    )
  );

  withdrawParticipantFromStudyAndRemoveDataSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.withdrawParticipantFromStudyAndRemoveDataSuccess),
        tap(({ umParticipant }) => {
          this.toastrService.success(`Successfully removed Participant from the Study and removed all his data`);
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  withdrawParticipantFromStudyAndRemoveDataError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.withdrawParticipantFromStudyAndRemoveDataError),
        tap(() => this.toastrService.error('Error while delete Participant from the Study'))
      ),
    { dispatch: false }
  );

  withdrawParticipantFromStudyAndKeepDataAnonymous$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.withdrawParticipantFromStudyAndKeepDataAnonymous),
      switchMap(({ studyId, participantId }) => {
        return this.umParticipantService.withdrawParticipantFromStudyAndUnlinkData(studyId, participantId).pipe(
          map((umParticipant) =>
            umParticipantActions.withdrawParticipantFromStudyAndKeepDataAnonymousSuccess({ umParticipant })
          ),
          catchError(() => of(umParticipantActions.withdrawParticipantFromStudyAndKeepDataAnonymousError()))
        );
      })
    )
  );

  withdrawParticipantFromStudyAndKeepDataAnonymousSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.withdrawParticipantFromStudyAndKeepDataAnonymousSuccess),
        tap(({ umParticipant }) => {
          this.toastrService.success(`Successfully removed Participant from the Study and kept his data as Anonymous`);
          this.dialog.closeAll();
        })
      ),
    { dispatch: false }
  );

  withdrawParticipantFromStudyAndKeepDataAnonymousError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.withdrawParticipantFromStudyAndKeepDataAnonymousError),
        tap(() => this.toastrService.error('Error while delete Participant from the Study'))
      ),
    { dispatch: false }
  );

  downloadParticipantHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.downloadParticipantHistory),
      switchMap(({ participantId }) => {
        return this.umParticipantService.downloadParticipantHistory(participantId).pipe(
          map((result) => {
            const anchor = document.createElement('a');
            anchor.download = `Export-participant-${participantId}-history.csv`;
            anchor.href = window.URL.createObjectURL(new Blob([result], { type: 'text/csv' }));
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            return umParticipantActions.downloadParticipantHistorySuccess();
          }),
          catchError(() => of(umParticipantActions.downloadParticipantHistoryError()))
        );
      })
    )
  );

  downloadParticipantHistoryError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.downloadParticipantHistoryError),
        tap(() => this.toastrService.error('Unable to download participant history.'))
      ),
    { dispatch: false }
  );

  downloadStudyAnswers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.downloadStudyAnswers),
      switchMap(({ participantId, studyId }) => {
        return this.umParticipantService.downloadParticipantStudyAnswers(participantId, studyId).pipe(
          map((result) => {
            const anchor = document.createElement('a');
            anchor.download = `Export-participant-${participantId}-study-${studyId}-answers.csv`;
            anchor.href = window.URL.createObjectURL(new Blob([result], { type: 'text/csv' }));
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            return umParticipantActions.downloadStudyAnswersSuccess();
          }),
          catchError(() => of(umParticipantActions.downloadStudyAnswersError()))
        );
      })
    )
  );

  downloadStudyAnswersError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.downloadStudyAnswersError),
        tap(() => this.toastrService.error('Unable to download participant study answers.'))
      ),
    { dispatch: false }
  );

  downloadParticipantData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(umParticipantActions.downloadParticipantData),
      switchMap(({ participantId }) => {
        return this.umParticipantService.downloadAllParticipantData(participantId).pipe(
          map((result) => {
            const anchor = document.createElement('a');
            anchor.download = `Export-participant-${participantId}-data.csv`;
            anchor.href = window.URL.createObjectURL(new Blob([result], { type: 'text/csv' }));
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            return umParticipantActions.downloadParticipantDataSuccess();
          }),
          catchError(() => of(umParticipantActions.downloadParticipantDataError()))
        );
      })
    )
  );

  downloadParticipantDataError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(umParticipantActions.downloadParticipantDataError),
        tap(() => this.toastrService.error('Unable to download participant data.'))
      ),
    { dispatch: false }
  );

  constructor(
    private actions$: Actions,
    private umParticipantService: UmParticipantService,
    private dashboardCamelCasePipe: DashboardCamelCasePipe,
    private toastrService: ToastrService,
    private router: Router,
    private dialog: MatDialog
  ) {}
}
